import * as React from 'react';
import classnames from 'classnames';
import styled from '@emotion/styled';
import * as vars from '../../consts/variables';
import { hasSoldOut, priceAvailability } from '../../helpers/priceHelpers';
import { upperFirstLetter } from '../../helpers/string';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, IImage, ISoldOutBooking } from '../../interfaces';
import { PerPersonPriceSmall } from '../PerPersonPriceSmall';
import { getAllInPriceFormatted } from '../PriceString';
import { SHIP_IN_SHIP_CABIN } from '../../reducers/cruisePage/cruisePageMappers';

interface IProps {
    kind: string;
    price: number;
    allInPrice?: number;
    discount: number;
    currency: string;
    isLastItem: boolean;
    image: IImage;
    cabins: ICabinInfo[];
    sailNid: number;
    labelCaption: string;
    isPriceShown: boolean;
    learnMoreMode?: boolean;
    includesFlight: boolean;
    onClick(): void;
    departure?: number;
    soldOutBooking: ISoldOutBooking;
    discountThreshold: number;
    cabinId?: number;
    cheapestCabinId?: number;
    onCabinSelection?: (event: any) => void;
    isSelectedCabin?: boolean;
    operator: string;
}

const RadioInputStyled = styled.input`
    appearance: none;
    width: 20px;
    height: 20px;
    border: solid 2px ${({ theme }) => theme.colors.iron[100]};
    border-radius: 50%;

    &[type='radio']:focus,
    &.checked {
        border-radius: 50%;
        background: ${({ theme }) => theme.icon.brandColor1};
        border: 2px solid ${({ theme }) => theme.colors.white[100]};
        box-shadow: 0px 0px 0px 2px ${({ theme }) => theme.icon.brandColor1};
    }

    &[type='radio']:focus {
        outline: none;
        outline-offset: 0;
    }
`;

const StyledLabel = styled.label`
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    margin-bottom: 0;
    gap: 8px;
    width: 100%;
`;
const CabinTitleWrapper = styled.div<{ disabled: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    justify-content: center;
    margin-right: auto;
    gap: 4px;

    color: ${({ theme, disabled }) => (disabled ? theme.colors.gunmetal[50] : 'initial')};
`;

const CabinTitle = styled.h4`
    margin: 0;
    font-size: 14px;
`;

export function CabinInfo(props: IProps) {
    const {
        kind,
        price,
        allInPrice,
        discount,
        currency,
        learnMoreMode,
        includesFlight,
        departure,
        soldOutBooking,
        cabinId,
        discountThreshold,
        isSelectedCabin,
    } = props;

    const cabinKind = `${upperFirstLetter(kind)}${kind !== 'suite' ? ' Cabin' : ''}`;
    const isDisabled = hasSoldOut(departure, soldOutBooking, price);
    const availablePrice = priceAvailability(departure, soldOutBooking, price, currency);

    const handleCabinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked) return;
        props.onCabinSelection(cabinId);
    };

    const displayCabinKind =
        kind === SHIP_IN_SHIP_CABIN ? `shipCabin${props.operator.toUpperCase()}` : cabinKind;
    const classes = classnames('cabinInfoBar', { cabinInfoBar__learnMore: learnMoreMode });

    return (
        <div className={classes}>
            <StyledLabel>
                {!learnMoreMode && (
                    <RadioInputStyled
                        className={isSelectedCabin ? 'checked' : ''}
                        checked={isSelectedCabin}
                        onChange={handleCabinChange}
                        type="radio"
                        disabled={isDisabled}
                    />
                )}
                <CabinTitleWrapper disabled={isDisabled}>
                    <CabinTitle>{__(displayCabinKind, 'dreamlines')}</CabinTitle>
                    {!learnMoreMode && (
                        <PerPersonPriceSmall
                            price={price}
                            priceString={availablePrice.string}
                            allInPriceString={getAllInPriceFormatted(allInPrice, currency)}
                            discount={discount}
                            discountThreshold={discountThreshold}
                            isHeader={true}
                            wrapperStyle={{ color: vars.black }} // Move component to sass
                            includesFlight={includesFlight}
                            onRequest={!availablePrice.valid}
                        />
                    )}
                </CabinTitleWrapper>
            </StyledLabel>
        </div>
    );
}
